import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '../../layout/memberPrimitives';
import type { MemberDashboardToday } from '../../../hooks/useMemberDashboard';

/**
 * A compact view of today's check-in — the trackers the member opted into,
 * whether each is logged, and the streak.
 *
 * The full Daily Bite surface is substantial (eight trackers, freezes, badges,
 * weekly reflection, correlations). None of that belongs on the dashboard: this
 * is a ten-second glance and a way back in.
 *
 * Hidden entirely when the member has opted out of every tracker — the strip is
 * a picture of what they chose to track, not a nag.
 */
export default function TodayStrip({ today }: { today: MemberDashboardToday }) {
  if (today.optedInCount === 0) return null;

  return (
    <section aria-labelledby="today-heading">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <h2
          id="today-heading"
          className="text-[11px] uppercase tracking-[0.22em]"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          Today
        </h2>
        {today.streak > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5"
            style={{ color: 'var(--melio-olive-deep)', backgroundColor: 'rgba(79, 93, 24, 0.15)' }}
          >
            Day {today.streak}
          </span>
        )}
      </div>

      <Link
        to="/today"
        className="flex items-center gap-4 flex-wrap px-4 py-3.5 melio-lift"
        style={{ border: '1px solid var(--melio-border)', backgroundColor: 'white' }}
      >
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5 list-none p-0 m-0 flex-1 min-w-0">
          {today.strip.map((item) => (
            <li key={item.id} className="flex items-center gap-1.5 text-sm">
              <span
                aria-hidden="true"
                style={{ color: item.logged ? 'var(--melio-olive-deep)' : 'var(--melio-border)' }}
              >
                {item.logged ? '✓' : '—'}
              </span>
              <span style={{ color: item.logged ? 'var(--melio-ink)' : 'var(--melio-text-muted)' }}>
                {item.label}
              </span>
              <span className="sr-only">{item.logged ? 'logged' : 'not logged'}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs tabular-nums" style={{ color: 'var(--melio-text-muted)' }}>
            {today.loggedCount} of {today.total}
          </span>
          <span className="text-xs font-semibold" style={{ color: 'var(--melio-olive-deep)' }}>
            {today.unlogged === 0 ? 'All logged' : "Finish today's check-in"}
          </span>
          <ChevronRightIcon size={14} />
        </div>
      </Link>
    </section>
  );
}
